import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { api, STATUSES, STATUS_META, DONE_STATUSES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { scanFeedback } from "@/lib/feedback";
import { scanTarget, scanFlow } from "@/pages/ScanJob";
import { StatusBadge, textOn } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Camera, CameraOff, CheckCircle2, AlertTriangle, XCircle, ScanLine, Trash2 } from "lucide-react";

const jobIdFrom = (text) => {
  const m = String(text).match(/\/scan\/([0-9a-f-]{36})/i) || String(text).match(/^([0-9a-f-]{36})$/i);
  return m ? m[1] : null;
};
const stockIdFrom = (text) => { const m = String(text).match(/\/stock\/scan\/([0-9a-f-]{36})/i); return m ? m[1] : null; };

const REGION = "bulk-scan-reader";

export default function BulkScan() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);
  const [manualTarget, setManualTarget] = useState(null);
  const scanner = useRef(null);
  const lastSeen = useRef({});
  const busy = useRef(false);
  const targetRef = useRef(null);
  targetRef.current = manualTarget;
  const autoRole = !!scanFlow(user?.role);

  const push = (entry) => setLog((l) => [{ ...entry, at: new Date() }, ...l].slice(0, 50));

  const handle = async (text) => {
    const sid = stockIdFrom(text);
    if (sid) { scanFeedback("success"); await stop(); nav(`/stock/bulk`); return; }
    const id = jobIdFrom(text);
    const now = Date.now();
    if (!id || busy.current || now - (lastSeen.current[id] || 0) < 4000) return;
    lastSeen.current[id] = now;
    busy.current = true;
    try {
      const { data: job } = await api.get(`/public/scan/${id}`);
      const to = autoRole ? scanTarget(user.role, job.status) : targetRef.current;
      if (!to) {
        scanFeedback("warn");
        push({ kind: "warn", job, msg: DONE_STATUSES.includes(job.status) ? "Job already closed" : autoRole ? "Already at your stage" : "Select a target status first" });
        return;
      }
      if (to === job.status) { scanFeedback("warn"); push({ kind: "warn", job, msg: `Already ${to}` }); return; }
      await api.patch(`/jobs/${id}`, { status: to, scan_mode: "bulk" });
      scanFeedback("success");
      push({ kind: "ok", job: { ...job, status: to }, from: job.status, to });
    } catch (e) {
      scanFeedback("error");
      const d = e?.response?.data?.detail;
      push({ kind: "err", job: { id, job_number: id.slice(0, 8) }, msg: typeof d === "string" ? d : e?.response?.status === 404 ? "Job not found" : "Update failed" });
    } finally { busy.current = false; }
  };

  const start = async () => {
    try {
      scanner.current = scanner.current || new Html5Qrcode(REGION, { verbose: false });
      await scanner.current.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 240, height: 240 } }, handle, () => {});
      setRunning(true);
    } catch (e) {
      toast.error("Camera not available. Allow camera access and try again.");
    }
  };
  const stop = async () => {
    try { await scanner.current?.stop(); } catch { /* scanner already stopped */ }
    setRunning(false);
  };
  useEffect(() => () => { scanner.current?.stop().catch(() => {}); }, []);
  useEffect(() => { window.__bulkScanHandle = handle; return () => { delete window.__bulkScanHandle; }; });

  const okCount = log.filter((l) => l.kind === "ok").length;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4" data-testid="bulk-scan-page">
      <div className="max-w-md mx-auto space-y-4">
        <div className="flex items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-3"><img src="/rozfa-logo-white.png" alt="Rozfa" className="h-8 w-auto" /><div className="text-xs font-mono text-slate-400 uppercase tracking-widest">Bulk Scan</div></div>
          <div className="text-xs text-slate-400" data-testid="bulk-scan-user">{user?.name} • {user?.role}</div>
        </div>

        <Card className="p-3 bg-white text-slate-900 space-y-2">
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1"><ScanLine className="w-3.5 h-3.5" /> Scan mode</div>
          {autoRole ? (
            <div className="text-sm" data-testid="bulk-scan-mode-auto">Auto by role: each scan → <span className="font-semibold">{scanFlow(user.role).join(" → ")}</span></div>
          ) : (
            <div className="space-y-1" data-testid="bulk-scan-mode-manual">
              <div className="text-sm">Pick the status every scanned job should move to:</div>
              <div className="grid grid-cols-2 gap-1.5">
                {STATUSES.map((s) => {
                  const c = STATUS_META[s]?.color || "#e2e8f0"; const active = manualTarget === s;
                  return <button key={s} type="button" onClick={() => setManualTarget(active ? null : s)} data-testid={`bulk-target-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`h-10 rounded-lg text-xs font-semibold border-2 ${active ? "ring-4 ring-cyan-400" : ""}`} style={{ backgroundColor: c, color: textOn(c), borderColor: c === "#ffffff" ? "#94a3b8" : c }}>{s}</button>;
                })}
              </div>
            </div>
          )}
        </Card>

        <div className="rounded-xl overflow-hidden bg-black border-2 border-slate-700 relative" style={{ minHeight: running ? 0 : 200 }}>
          <div id={REGION} data-testid="bulk-scan-reader" />
          {!running && <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm">Camera off</div>}
        </div>
        <Button onClick={running ? stop : start} className={`w-full h-12 font-semibold ${running ? "bg-rose-600 hover:bg-rose-700" : "bg-cyan-600 hover:bg-cyan-700"} text-white`} data-testid="bulk-scan-toggle">
          {running ? <><CameraOff className="w-4 h-4 mr-2" /> Stop Camera</> : <><Camera className="w-4 h-4 mr-2" /> Start Camera</>}
        </Button>

        <div className="flex items-center justify-between">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Scanned this session <Badge variant="secondary" className="ml-1 font-mono" data-testid="bulk-scan-count">{okCount} updated / {log.length}</Badge></div>
          {log.length > 0 && <button onClick={() => setLog([])} className="text-xs text-slate-400 flex items-center gap-1 hover:text-white" data-testid="bulk-scan-clear"><Trash2 className="w-3 h-3" /> Clear</button>}
        </div>
        <div className="space-y-2" data-testid="bulk-scan-log">
          {log.length === 0 && <Card className="p-6 bg-slate-900 border-slate-800 text-center text-slate-500 text-sm">Point the camera at a Job Sheet / Thermal Label QR to update jobs, or a Stock QR label to record Stock In / Out.</Card>}
          {log.map((l, i) => (
            <button key={i} onClick={() => l.job?.id && nav(`/scan/${l.job.id}`)} data-testid={`bulk-scan-entry-${l.job?.job_number}`}
              className={`w-full text-left rounded-lg p-3 flex items-center gap-3 border ${l.kind === "ok" ? "bg-emerald-950/60 border-emerald-700" : l.kind === "warn" ? "bg-amber-950/60 border-amber-700" : "bg-rose-950/60 border-rose-700"}`}>
              {l.kind === "ok" ? <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" /> : l.kind === "warn" ? <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" /> : <XCircle className="w-5 h-5 text-rose-400 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-mono font-bold text-sm flex items-center gap-2">{l.job?.job_number}{l.job?.status && <StatusBadge status={l.job.status} />}</div>
                <div className="text-xs text-slate-300 truncate">{l.kind === "ok" ? `${l.from} → ${l.to}` : l.msg}{l.job?.customer_name && ` • ${l.job.customer_name}`}</div>
              </div>
              <div className="text-[10px] font-mono text-slate-400">{l.at.toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
